import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import SiteCoreLinks from "@/components/SiteCoreLinks";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// EN mirror of /visa — visa guide hub for foreigners staying in Thailand
import { createFileRoute, Link } from "@tanstack/react-router";
import { speakableSchema, howToSchema } from "@/lib/aeo";
import { Plane, FileCheck2, Calendar } from "lucide-react";
import { TranslateVisaCrossLink } from "@/components/TranslateVisaCrossLink";
import { RelatedComparisons } from "@/components/RelatedComparisons";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { VISA_TYPES } from "@/data/visa-types";
import { AIAnswerBox } from "@/components/AIAnswerBox";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_EN } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Thailand Visa Guide — Work, Retirement, LTR, DTV | NYC Language Institute";
const DESC =
  "Complete Thailand visa guide for foreigners — Non-B work visa, LTR, DTV, retirement O/O-A/O-X. Free consultation — message us and get a same-day answer.";

const CAT_LABEL: Record<string, string> = {
  work: "Work & Business",
  investor: "Investor & Premium",
  retirement: "Retirement",
  family: "Family / Marriage",
  tourist: "Tourist & Short-stay",
  education: "Education",
  elite: "Elite / Long-term",
};

export const Route = createFileRoute("/en/visa/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("visa", "en", "https://nyclanguageinstitute.com/en/visa") }),
  head: ({ loaderData }: any) => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/visa", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/visa", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/visa", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/visa", "en")},
      { property: "og:url", content: `${SITE}/en/visa` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/visa` },
      ...hreflangLinks("/visa", "/en/visa"),
    ],
    scripts: [speakableSchema(),
      howToSchema({
        name: "How to prepare documents for Thailand visa (and outbound visas)",
        description: "Step-by-step document preparation for Thai Non-B, LTR, DTV, Retirement, Marriage visas + outbound Schengen, US, UK, AU visas — translation + apostille mapped per requirement.",
        inLanguage: "en", totalTime: "PT10D", estimatedCost: { currency: "THB", value: "8000" },
        steps: [
          { name: "Check visa-specific checklist", text: "Open the checklist matching your visa type (Non-B, LTR, DTV, Marriage, Retirement, Schengen, US, UK, AU).", url: `${SITE}/en/checklist` },
          { name: "Gather original documents", text: "House registration, birth certificate, marriage certificate, education credentials, bank statements, police check — as the checklist requires." },
          { name: "Translate with certified translators", text: "We translate to English (or destination language) — sworn translators registered with the Thai Ministry of Justice.", url: `${SITE}/en/translate` },
          { name: "MFA + Apostille / Legalization", text: "Submit to Thai MFA, then Apostille (Hague members) or Consular Legalization (non-Hague).", url: `${SITE}/en/apostille` },
          { name: "Submit at embassy / immigration", text: "Book your appointment — we offer walk-in support with full document set ready." },
        ],
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Thailand Visa Application Support",
          provider: { "@type": "LegalService", name: "NYC Legal & Notary Service Co., Ltd.", url: SITE },
          areaServed: "TH",
          inLanguage: "en",
          serviceType: "Visa Document Translation & Legalization",
        }),
      },
      entitySameAsSchema(),
      softwareApplicationSchema({ url: `${SITE}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing Thai visa consulting tooling for all nationalities." }),
      claimReviewSchema(COMMON_CLAIM_REBUTTALS_EN(`${SITE}/en/visa`)),
    
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
  }),
  component: EnVisaHub,
});

function EnVisaHub() {
  const cats = Object.keys(CAT_LABEL);
  return (
    <main className="min-h-screen bg-white">
      <div className="mx-auto max-w-6xl px-4 pt-6">
        <AIAnswerBox
          question="What Thai visa services does NYC support?"
          answer={`NYC supports ${VISA_TYPES.length}+ Thailand visa types (Non-B, LTR, DTV, Elite, Retirement, Marriage, ED) for 50+ nationalities — certified translation, MFA legalization / Apostille and embassy walk-in support, all in one place. Reply within 15 minutes.`}
          stats={[
            { label: "Visa types", value: `${VISA_TYPES.length}+` },
            { label: "Nationalities", value: "50+" },
            { label: "Reply", value: "15 min" },
            { label: "DBD", value: "0435567000061" },
          ]}
          sourceUrl={`${SITE}/en/visa`}
          lang="en"
        />
      </div>
      <section className="bg-navy-deep px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Thailand visa guide — <span className="text-amber-300">{VISA_TYPES.length} visa types</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            We don't file visas — we prepare the supporting documents that win visas. Certified translation of your
            birth certificate, marriage certificate, education credentials, bank statements, and police checks, plus
            apostille / legalization when your destination embassy requires it.
          </p>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-5xl gap-5 sm:grid-cols-3">
          {[
            { icon: Plane, t: "Immigration-ready", d: "Documents accepted by Thai Immigration, MFA, and 50+ embassies." },
            { icon: FileCheck2, t: "Per-visa checklist", d: "Side-by-side requirements with sample documents." },
            { icon: Calendar, t: "Fast turnaround", d: "Standard 24–48h, rush 4–8h. We coordinate MFA + embassy slots." },
          ].map((c) => (
            <article key={c.t} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <c.icon className="h-7 w-7 text-amber-600" />
              <h2 className="mt-3 text-base font-bold text-navy-deep">{c.t}</h2>
              <p className="mt-2 text-sm text-slate-600">{c.d}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 px-4 py-14">
        <div className="mx-auto max-w-6xl space-y-10">
          {cats.map((cat) => {
            const items = VISA_TYPES.filter((v) => v.category === cat);
            if (!items.length) return null;
            return (
              <div key={cat}>
                <h2 className="text-2xl font-extrabold text-navy-deep">{CAT_LABEL[cat]}</h2>
                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((v) => (
                    <article key={v.id} className="rounded-xl border border-slate-200 bg-white p-5">
                      <p className="text-xs uppercase tracking-widest text-amber-700">{v.code}</p>
                      <h3 className="mt-1 text-base font-bold text-navy-deep">{v.name}</h3>
                      <p className="mt-2 text-xs text-slate-600">{v.durationInitial} · max {v.durationMax}</p>
                      <p className="mt-2 text-xs text-slate-500">Gov fee {v.govFee} · processing {v.processingTime}</p>
                    </article>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="px-4 py-14">
        <div className="mx-auto max-w-5xl rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-700">
          Need translated supporting documents for your visa file? Start with our{" "}
          <Link to="/en/visa/documents" className="font-bold text-amber-700 hover:underline">
            guide to the six visa document families
          </Link>
          , browse{" "}
          <Link to="/en/services" className="font-bold text-amber-700 hover:underline">/en/services</Link>{" "}
          or request a quote at <Link to="/en/quote" className="font-bold text-amber-700 hover:underline">/en/quote</Link>.
        </div>
      </section>

      <TranslateVisaCrossLink mode="from-visa" locale="en" />
      <RelatedComparisons category="visa" locale="en" />
          <SiteCoreLinks />
    
      <PlanningToolsLinks locale="en" />
      </main>
  );
}
